/* eslint-disable lingui/no-unlocalized-strings -- caminhos HTTP, chaves do fio e rótulos de log, não texto de UI */
import { z } from 'zod';

import { AMORTIZATION_KEYS, pickKeys } from '@/shared/amortization';
import { formatCnpj } from '@/shared/format/cnpj';
import { creditApi, TIMEOUTS, type ApiRequestConfig } from '@/shared/http';

// Fio das ações da lista (trilha "lista"): Gerar relatório (CNAB/remessa), Clonar, Associar empregador em
// lote, o grupo Compliance_Admin e o resultado de "Importar garantia de veículos". Cada chamada repete a
// do legado (U02) com `arquivo:linha`; os rótulos de tela ficam em `screens/list-options.tsx`.

function parseWire<S extends z.ZodType>(schema: S, data: unknown, label: string): z.output<S> {
  const parsed = schema.safeParse(data);
  if (parsed.success) return parsed.data;
  if (!import.meta.env.PROD) console.warn(`${label} fora do contrato`, parsed.error.issues);
  return data as z.output<S>;
}

// ─── Gerar relatório (CreditNoteCnab/*, services/cnab/cnab.ts) ─────────────────

/** Valores do "Tipo de relatório" que o formulário grava (`cnab`), na ordem das opções do legado (CreditNoteCnab.tsx:30-58). */
export const REPORT_DEFAULT = ['Cnab400', 'Cnab400itau', 'Cnab400B', 'Cnab444', 'Cnab444REAG', 'Cnab500', 'Cnab550', 'Cnab600', 'Cnab800', 'Cnab600Kovr', 'CnabDigiMais', 'Cnab600HeadInvest'] as const;
/** Acrescentadas para root sem `FORM_FIELDS.cnabReports` (CreditNoteCnab.tsx:47-54). */
export const REPORT_ROOT = ['liquidationFile', 'Vortx', 'CnabAXA', 'CnabAXACancel'] as const;
/** Só com uma Nota Comercial selecionada e sem config (CreditNoteCnab.tsx:56-58). */
export const REPORT_COMMERCIAL_PAPER = 'NCB3';
/** Alcançáveis só por `FORM_FIELDS.cnabReports` (CreditNoteCnabContainer.tsx:186-189). */
export const REPORT_CONFIG_ONLY = ['StarPeculio', 'StarAssistenciaFinanceira'] as const;

/** O campo "Cessionário" aparece para estes (CreditNoteCnab.tsx:61-68). */
export const REPORT_FUND_VISIBLE: readonly string[] = ['Cnab444REAG', 'Cnab444', 'Cnab500', 'Cnab550', 'Cnab600', 'Cnab600Kovr'];
/** O yup só exige o cessionário nestes (CreditNoteCnabSchema.ts:4). */
export const REPORT_FUND_REQUIRED: readonly string[] = ['Cnab444REAG', 'Cnab444', 'Cnab500', 'Cnab550'];
/** `requiredFundId`: o `?fundId=` só vai nestes, e só com valor (CreditNoteCnabContainer.tsx:168-176; cnab.ts:72-74). */
const REPORT_FUND_QUERY: readonly string[] = ['Cnab444REAG', 'Cnab444', 'Cnab500', 'Cnab550', 'Cnab600', 'Cnab800', 'Cnab600Kovr'];

/** `cnabTypes` (CreditNoteCnabContainer.tsx:150-166): valor do formulário → `type` do `postGenerateCnabs`. */
const GENERATE_TYPES: Readonly<Record<string, string>> = {
  liquidationFile: 'LiquidationFileWebCred',
  Vortx: 'Vortx',
  Cnab400: 'Cnab400',
  Cnab400B: 'Cnab400B',
  Cnab444REAG: 'Cnab444REAG',
  Cnab444: 'Cnab444',
  Cnab500: 'Cnab500',
  Cnab550: 'Cnab550',
  Cnab800: 'Cnab800',
  CnabAXA: 'CnabAXA',
  CnabDigiMais: 'CnabDigimais',
  CnabAXACancel: 'CnabAXACancel',
  Cnab600: 'Cnab600',
  Cnab600Kovr: 'Cnab600Kovr',
  Cnab600HeadInvest: 'Cnab600HeadInvest',
};

/**
 * Caminho de cada `type` (cnab.ts:54-70). Os sem barra inicial do legado (`Cnab/Cnab400B`,
 * `Cnab/cnabAxaAdesao`, `Cnab/cnabAxaCancel`) resolvem no mesmo endereço pelo `baseURL` do axios.
 */
const GENERATE_PATHS: Readonly<Record<string, string>> = {
  Cnab400: '/Cnab400',
  Cnab400B: '/Cnab/Cnab400B',
  Cnab444: '/Cnab/cnab444',
  Cnab444REAG: '/Cnab444REAG',
  Cnab500: '/Cnab500',
  Cnab550: '/Cnab550',
  CnabDigimais: '/Cnab/cnabDigiMais',
  Cnab600: '/Cnab/cnab600',
  Cnab800: '/Cnab/cnab800',
  CnabAXA: '/Cnab/cnabAxaAdesao',
  CnabAXACancel: '/Cnab/cnabAxaCancel',
  LiquidationFileWebCred: '/LiquidationFileWebCred',
  Vortx: '/vortx',
  Cnab600Kovr: '/Cnab/Cnab600Kovr',
  Cnab600HeadInvest: '/Cnab/Cnab600HeadInvest',
};

/** As quatro que não passam pelo `postGenerateCnabs` (cnab.ts:18-45): sem timeout próprio. */
const OTHER_REPORTS: Readonly<Record<string, (ids: readonly string[]) => { readonly url: string; readonly body: unknown }>> = {
  NCB3: (ids) => ({ url: `/Cnab/NCB3/${ids[0] ?? ''}`, body: {} }),
  StarPeculio: (ids) => ({ url: '/Cnab/StarPeculio', body: ids }),
  StarAssistenciaFinanceira: (ids) => ({ url: '/Cnab/StarAssistenciaFinanceira', body: ids }),
  Cnab400itau: (ids) => ({ url: '/Cnab/cnab400itau', body: ids }),
};

export interface ReportRequest {
  readonly url: string;
  readonly body: unknown;
  readonly params?: { readonly fundId: string };
  readonly timeout?: number;
  /** `type` do `postGenerateCnabs`, que vira o título do toast do legado ("Cnab400 gerado com sucesso"). */
  readonly generated?: string;
}

/**
 * A chamada de um "Tipo de relatório" (CreditNoteCnabContainer.tsx:114-193). `undefined` quando o valor
 * não tem integração (um `FORM_FIELDS.cnabReports` com valor fora das listas): o legado não chamava nada.
 */
export function reportRequest(value: string, ids: readonly string[], fundId: string | null | undefined): ReportRequest | undefined {
  const generated = GENERATE_TYPES[value];
  if (generated) {
    const url = GENERATE_PATHS[generated] ?? '';
    const body = generated === 'Cnab800' ? { creditNotes: ids, batchAssignmentId: null } : ids;
    return {
      url,
      body,
      ...(fundId && REPORT_FUND_QUERY.includes(value) ? { params: { fundId } } : {}),
      timeout: TIMEOUTS.s90,
      generated,
    };
  }
  return OTHER_REPORTS[value]?.(ids);
}

export const reportResultSchema = z.looseObject({ tempUrl: z.string().min(1) });
export type ReportResult = z.infer<typeof reportResultSchema>;

/** Envia o relatório e devolve o `tempUrl` que o legado abria numa aba nova. */
export async function generateReport(request: ReportRequest): Promise<ReportResult> {
  const config: ApiRequestConfig = {
    ...(request.params ? { params: request.params } : {}),
    ...(request.timeout ? { timeout: request.timeout } : {}),
  };
  const data = await creditApi.post(request.url, request.body, config);
  return parseWire(reportResultSchema, data, `POST ${request.url}`);
}

// ─── Clonar (creditNoteListActions.tsx:672-679; creditNote.ts:151-252, 313-318) ──

/** Chaves de `CreditNoteOpBaseModel` (genericTypes.ts:79-114): a whitelist do `toDataModel` (campos sem valor inclusos, CRA). */
const CREATE_KEYS = [
  'productId',
  'fundId',
  'warranty',
  'emissionDate',
  'amortization',
  'personId',
  'liquidationType',
  'invoiceBarCode',
  'beneficiaryId',
  'collateral',
  'observations',
  'status',
  'bankAccountId',
  'tokenName',
  'isRegistered',
  'bulkAssignment',
  'invoiceValue',
  'invoiceNumber',
  'changeBankAccountId',
  'issueNumber',
  'emissionNumber',
  'quantity',
  'unitPrice',
  'assignmentCalculation',
  'relatedPersonCreditNote',
  'uploads',
  'paymentScheduleItems',
  'unicoSignScore',
  'registrationNumber',
  'transitoryBankAccountId',
  'transitoryBankAccountOpenAccount',
  'liquidationMode',
  'liquidationSchedule',
  'insurance',
  'creditNoteOpIds',
] as const;

/** O `duplicateCreditNote` apaga estas antes do mapeamento (creditNote.ts:314-315). */
const CLONE_DROPPED = ['status', 'paymentScheduleItems', 'timeline', 'creditNoteNo', 'id', 'uploads'];

/** `LiquidationScheduleRequestModel` (LiquidationScheduleRequestModel.ts:17-25). */
const LIQUIDATION_SCHEDULE_KEYS = ['amountInCents', 'informedDueDate', 'pixQrCode', 'bankSlip', 'pixKey', 'transfer', 'description'];

/** Os `*CreateModel` de garantia que o `editOrCreateCreditNote` mapeia (creditNote.ts:192-243; WarrantyModel/*.ts). */
const WARRANTY_KEYS: Readonly<Record<string, readonly string[]>> = {
  Vehicle: ['warrantyType', 'renavam', 'typeOfVehicle', 'chassis', 'board', 'supplier', 'taxNumberOfSupplier', 'brand', 'color', 'model', 'yearOfManufacture', 'yearOfModel', 'typeOfFuel', 'plateState', 'operationState', 'downPayment', 'sellerCompanyName', 'sellerRegistrationNumber', 'valueInCash', 'subtotalAmount', 'totalAmountFinancedTaxFree'],
  InvoiceFactoring: ['warrantyType', 'payerName', 'payerRegistrationNumber', 'contractNumber', 'invoiceNumber', 'invoiceValue', 'discountValue', 'presentValue', 'dueDate', 'paymentAmountInCents', 'paymentItemIndex', 'paymentItemTotal', 'yourNumber', 'storeCode', 'productCode', 'creditType', 'identification', 'emissionDate', 'index', 'payerDescription', 'payerAdress', 'payerZipCodeAdress', 'contractFee'],
  HomeEquity: ['warrantyType', 'registryOffice', 'registrationNumber', 'width', 'length', 'addressName', 'zipCodeAddress', 'city', 'uf', 'district', 'addressNumber', 'complementAddress', 'propertyFeatures'],
  JudiciaryProcess: ['warrantyType', 'number', 'court', 'valueOfCause', 'claimant', 'claimed', 'typeOfProcess'],
  Factoring: ['warrantyType', 'totalValue', 'register', 'type', 'splitMethodType', 'paymentNetworks', 'constituted'],
  DataprevEmployee: ['warrantyType', 'dataprev_EmployerName', 'dataprev_EmployeeCode', 'dataprev_EmployerRegistrationCode', 'dataprev_EmployerRegistrationNumber', 'dataprev_EmployeeAdmissionDate', 'dataprev_DiscountStartPeriod', 'totalValue', 'dataprev_HasWarranty', 'dataprev_WarrantyFgtsBalance', 'dataprev_WarrantyFgtsRescissionPenalty', 'dataprev_WarrantyRescissionBenefitPercentage'],
  PublicPayroll: ['warrantyType', 'employeeCode', 'agreementCode', 'employeerInstCode', 'payrollAgreementId', 'governmentDepartmentCode', 'payrollAgreementName', 'warrantyCode', 'paymentTransferDay', 'paymentAmountInCents', 'employmentStatusCode', 'employmentStatusDescription', 'previousAdeNumber', 'warrantyDeadline', 'warrantyOption', 'warrantyOptionDisplay', 'simulationRegistrationNumber', 'termInMonths', 'governmentDepartmentWarrantyStatusDisplay', 'payingSourceName', 'payingSourceRegistrationNumber'],
  Consortium: ['warrantyType', 'administratorName', 'administratorRegistrationNumber', 'quota', 'group'],
};

type Json = Record<string, unknown>;

const isRecord = (value: unknown): value is Json => typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * `cleanDocumentValue` (formFieldValidator.ts:139-142) sem apagar letras: tira só os separadores (`.`, `-`, `/` e
 * espaço, como o `stripDocumentSeparators` de `shared/format`). Com `CNPJ_ALPHANUMERIC`, ligada em STG, é o
 * resultado do legado; a flag não muda mais nada, porque `\D` faria um CNPJ com letra virar outro documento.
 */
export function cleanDocument(value: string, _alphanumeric?: boolean): string {
  return value.replace(/[.\-/\s]/g, '');
}

interface Commission {
  readonly name?: unknown;
  readonly amount?: unknown;
  readonly type?: unknown;
  readonly baseValue?: unknown;
}

/** `mergeAgentCommissions` (creditNote.ts:96-149): aplica a comissão singular (editada) sobre a lista do GET. */
export function mergeAgentCommissions(registered: unknown, edited: unknown): unknown {
  if (!Array.isArray(registered) || registered.length === 0) return registered;
  if (!isRecord(edited)) return registered;
  const commission = edited as Commission;
  const editedAmount = Number(commission.amount ?? 0);
  if (!Number.isFinite(editedAmount)) return registered;
  const editedName = commission.name;
  if (!editedName) return registered;
  const list = registered as Commission[];
  const index = list.findIndex((item) => item.name === editedName);
  if (index < 0) return editedAmount > 0 ? [...list, edited] : registered;
  if (Number(list[index]?.amount ?? 0) === editedAmount) return registered;
  if (editedAmount <= 0) return list.filter((_, position) => position !== index);
  return list.map((item, position) => (position === index ? { ...item, amount: commission.amount, type: commission.type, baseValue: commission.baseValue } : item));
}

/** A amortização com tipo sem classe no legado quebra (`new undefined()`), e o clonar cai no erro. */
export class UnsupportedAmortizationError extends Error {
  override readonly name = 'UnsupportedAmortizationError';
  readonly amortizationType: string;
  constructor(amortizationType: unknown) {
    const type = typeof amortizationType === 'string' ? amortizationType : '';
    super(`amortizationType sem classe de criação: ${type}`);
    this.amortizationType = type;
  }
}

function toAmortization(data: Json): Json {
  const type = typeof data['amortizationType'] === 'string' ? data['amortizationType'].toLowerCase() : undefined;
  const keys = type ? AMORTIZATION_KEYS[type] : undefined;
  if (!keys) throw new UnsupportedAmortizationError(data['amortizationType']);
  const amortization = pickKeys(data, keys);
  if (type !== 'discount') amortization['agentCommissions'] = mergeAgentCommissions(amortization['agentCommissions'], amortization['agentCommission']);
  return amortization;
}

function toWarranty(item: unknown, alphanumeric: boolean): unknown {
  if (!isRecord(item)) return item;
  const type = item['warrantyType'];
  const keys = typeof type === 'string' ? WARRANTY_KEYS[type] : undefined;
  if (!keys) return item;
  const warranty = pickKeys(item, keys);
  const clean = (key: string) => {
    const value = warranty[key];
    if (value && (typeof value === 'string' || typeof value === 'number')) warranty[key] = cleanDocument(String(value), alphanumeric);
  };
  if (type === 'Vehicle') {
    clean('sellerRegistrationNumber');
    clean('taxNumberOfSupplier');
  }
  if (type === 'InvoiceFactoring') clean('payerRegistrationNumber');
  if (type === 'DataprevEmployee') clean('dataprev_EmployerRegistrationNumber');
  return warranty;
}

/**
 * Corpo do `POST /CreditNote` do Clonar: o `GET` da operação sem as chaves apagadas, pela whitelist de
 * criação, com a amortização, o roteiro e as garantias mapeados, e `paymentScheduleItems: null`
 * (sempre apagado antes, creditNote.ts:245-251). Lança `UnsupportedAmortizationError` como o legado.
 */
export function toCloneBody(read: Json, alphanumeric: boolean): Json {
  const data: Json = { ...read };
  for (const key of CLONE_DROPPED) Reflect.deleteProperty(data, key);
  const body = pickKeys(data, CREATE_KEYS);
  if (body['amortization']) body['amortization'] = isRecord(body['amortization']) ? toAmortization(body['amortization']) : body['amortization'];
  const schedule = body['liquidationSchedule'];
  if (Array.isArray(schedule) && schedule.length > 0) body['liquidationSchedule'] = (schedule as unknown[]).map((item) => (isRecord(item) ? pickKeys(item, LIQUIDATION_SCHEDULE_KEYS) : item));
  const warranty = body['warranty'];
  if (Array.isArray(warranty)) body['warranty'] = warranty.map((item) => toWarranty(item, alphanumeric));
  const items = body['paymentScheduleItems'];
  return { ...body, paymentScheduleItems: Array.isArray(items) && items.length > 0 ? items : null };
}

const rawCreditNoteSchema = z.looseObject({ id: z.string() });

/** `getCreditNoteById` (creditNote.ts:278-281) sem o schema da tela: o Clonar copia o registro como veio. */
export async function getCreditNoteRaw(id: string, signal?: AbortSignal): Promise<Json> {
  const data = await creditApi.get<unknown>(`/CreditNote/${id}`, signal ? { signal } : {});
  return parseWire(rawCreditNoteSchema, data, 'GET /CreditNote/{id}');
}

export const createdIdSchema = z.string().min(1);

/** `editOrCreateCreditNote(data, 'nova', token, tenant)` (creditNote.ts:151-164, 253-258): `POST /CreditNote?tenant=`, devolve o id novo. */
export async function createCreditNote(body: Json): Promise<string> {
  const data = await creditApi.post('/CreditNote', body, { withTenant: true });
  return parseWire(createdIdSchema, data, 'POST /CreditNote');
}

/** O Clonar inteiro: lê, mapeia e cria (creditNoteListActions.tsx:672-679). */
export async function cloneCreditNote(id: string, alphanumeric: boolean): Promise<string> {
  const read = await getCreditNoteRaw(id);
  return createCreditNote(toCloneBody(read, alphanumeric));
}

// ─── Associar empregador em lote (LinkEmployerContainer.tsx:40-64) ─────────────

export interface ListLinkEmployerValues {
  employerName: string;
  employerRegistrationNumber: string;
  includeWebCred: boolean;
}

/** Uma entrada em `ops` por operação selecionada, com o CNPJ com a máscara do campo (U21), como o detalhe. */
export function listLinkEmployerBody(values: ListLinkEmployerValues, creditNoteNos: readonly number[]) {
  const employerRegistrationNumber = formatCnpj(values.employerRegistrationNumber) ?? values.employerRegistrationNumber;
  return {
    includeWebCred: values.includeWebCred,
    ops: creditNoteNos.map((creditNoteNo) => ({ employerName: values.employerName, employerRegistrationNumber, creditNoteNo })),
  };
}

// ─── Grupo Compliance_Admin (CreditNoteListButtons.tsx:104-113, 227-230) ───────

export const COMPLIANCE_ADMIN_GROUP = 'Compliance_Admin';

/** Os grupos vêm do `listUserGroups` de `modules/user` (`GET /User/{id}/Groups?page=0&size=20`, userContext.tsx:234-262). */
export const isComplianceAdmin = (groups: { readonly data: readonly { readonly groupName?: string | null }[] } | undefined) => groups?.data.some((group) => group.groupName === COMPLIANCE_ADMIN_GROUP) ?? false;

// ─── Importar garantia de veículos (creditNoteListActions.tsx:435-467) ─────────

/** `ImportVehicleWarrantyResponse` (types/importVehicleWarranty.ts:1-4). */
export const vehicleWarrantyImportSchema = z.looseObject({
  linhasNaoLidas: z.array(z.string()).nullish(),
  operacoesNaoEncontradas: z.array(z.string()).nullish(),
});
export type VehicleWarrantyImport = z.infer<typeof vehicleWarrantyImportSchema>;

export const parseVehicleWarrantyImport = (data: unknown) => parseWire(vehicleWarrantyImportSchema, data, 'POST /CreditNote/ImportVehicleWarranty');

/** O legado aceita `.csv` pelo nome ou pelo tipo (creditNoteListActions.tsx:441-448). */
export const VEHICLE_WARRANTY_ACCEPT = '.csv,text/csv,application/csv';

// ─── Enviar para carteira em lote (creditNoteListActions.tsx:252-288, 680-693) ──

/** O popup só oferece "Sim, confirmar" com todas em Encerrado (`onlyFinishedStatusSelected`, :103-105, 683-689). */
export const onlyFinishedSelected = (rows: readonly { readonly status: string }[]) => rows.every((row) => row.status.toLowerCase() === 'finished');

/** `isAlreadyInWalletManagementError` (:251-255): o detalhe da API diz que a operação já consta na carteira. */
export const isAlreadyInWalletManagement = (detail: string) => /consta na gest[aã]o de carteira/i.test(detail);

// ─── Iniciar cessão em lote (GenerateAssignmentForm*, creditNoteListActions.tsx:179-197) ──

/** `assignmentCalculationModeOptions` (services/creditNote/options/index.ts:1-6). */
export const ASSIGNMENT_CALCULATION_MODES = ['Default', 'InitialValue', 'PresentValueCreditNote', 'PresentValuePayment'] as const;
/** "Tipo de taxa" (GenerateAssignmentForm.tsx:70-75). */
export const ASSIGNMENT_CALCULATION_TYPES = ['Absolute', 'Percentage'] as const;

export interface StartBatchAssignmentValues {
  /** "Atualizar e calcular pelo ágio": estado local do legado (`agioDesagio`), só para root. */
  premium: boolean;
  /** Centavos (`CurrencyFormField`, número) no "Absolute"; fração em string (`PercentageFormField`, '0.015') no resto. */
  assignmentCalculation: { mode: string | null; type: string | null; amount: number | string | null };
  sendEmail: boolean;
  ignoreOverDuePayment: boolean;
}

/** `generateAssignmentDefaultValues` (GenerateAssignmentFormSchema.ts:30-38). */
export const startBatchAssignmentDefaults = (): StartBatchAssignmentValues => ({
  premium: false,
  assignmentCalculation: { mode: null, type: null, amount: null },
  ignoreOverDuePayment: true,
  sendEmail: false,
});

/**
 * Os valores do `onSubmit` (GenerateAssignmentFormContainer.tsx:16-23), depois do cast do `yupResolver`: o `amount`
 * vira string (`string()` no schema, GenerateAssignmentFormSchema.ts:7). Sem ágio, `assignmentCalculation: null`,
 * que o `onGenerateAssignment` apaga do corpo (`toBatchAssignmentBody`).
 */
export function startBatchAssignmentValues(values: StartBatchAssignmentValues) {
  const { mode, type, amount } = values.assignmentCalculation;
  return {
    assignmentCalculation: values.premium ? { amount: amount === null ? null : String(amount), type, mode } : null,
    sendEmail: values.sendEmail,
    ignoreOverDuePayment: values.ignoreOverDuePayment,
  };
}
